import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../../../common/table/ComonTable';
import SectionHeaderBar from '../../../../common/pageheading/SectionHeaderBar';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import apiServices from '../../../../../services/apiService';
import { exportToExcel } from '../../../../../utils/excelUtils';
import SearchBar from '../../../../common/formfields/SearchBar';
import { useSelector } from 'react-redux';

const ParkingReport = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [parkingData, setParkingData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const { user } = useSelector((state) => state.auth);

    const fetchParkingData = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ParkingBookingDetails/GetAllParkingBookingDetails`
            });

            if (response.success && response.data) {
                let data = response.data?.data || response.data || [];

                // If this needs to be site-filtered based on logged-in user like Angular
                // data = data.filter((item) => item.SiteId === siteId);

                setParkingData(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching parking data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParkingData();
    }, []);

    const handleExport = () => {
        const excelHeaders = [
            { label: "Site", key: "SiteName" },
            { label: "Wing", key: "WingName" },
            { label: "Floor", key: "FloorName" },
            { label: "Owner Name", key: "OwnerName" },
            { label: "Mobile Number", key: "MobileNumber" },
            { label: "Vehicle Number", key: "VehicleNumber" },
            { label: "Vehicle Type", key: "VehicleTypeName" },
            { label: "Parking Type", key: "ParkingType" },
            { label: "Parking Slot", key: "SlotName" },
            { label: "Amount", key: "Amount" },
            { label: "Start Date", key: "StartTime" },
            { label: "End Date", key: "EndTime" },
            { label: "Status", key: "IsActive" },
        ];

        exportToExcel(parkingData, excelHeaders, `Parking_Report`);
    };

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const onPageChange = (page) => {
        setCurrentPage(page);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "SITE", key: "SiteName" },
        { label: "WING", key: "WingName" },
        { label: "FLOOR", key: "FloorName" },
        { label: "OWNER NAME", key: "OwnerName" },
        { label: "MOBILE", key: "MobileNumber" },
        { label: "VEHICLE NO", key: "VehicleNumber" },
        { label: "VEHICLE TYPE", key: "VehicleTypeName" },
        { label: "PARKING TYPE", key: "ParkingType" },
        { label: "SLOT", key: "SlotName" },
        { label: "AMOUNT", key: "Amount" },
        { label: "START DATE", key: "StartTime" },
        { label: "END DATE", key: "EndTime" },
        { label: "STATUS", key: "IsActive" },
    ];

    const renderCell = (key, row) => {
        switch (key) {
            case "no":
            case "SiteName":
            case "WingName":
            case "FloorName":
            case "OwnerName":
            case "MobileNumber":
            case "VehicleNumber":
            case "VehicleTypeName":
            case "ParkingType":
            case "SlotName":
            case "Amount":
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
            case "StartTime":
            case "EndTime":
                return <span className="text-xs font-normal text-[#64748B]">
                    {row[key] ? new Date(row[key]).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                </span>;
            case "IsActive":
                let statusColor = "bg-gray-100 text-gray-600";
                if (row.IsActive === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (row.IsActive === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {row.IsActive || "—"}
                    </span>
                );
            default:
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const headerAction = (
        <div className="flex flex-wrap items-center gap-3">
            <SearchBar
                onSearch={setSearchQuery}
                placeholder="Search parking bookings..."
                className="w-48"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-[#0F172A] focus:outline-none focus:ring-1 focus:border-gray-400"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
            <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B] text-xs font-bold rounded-lg hover:bg-white hover:text-[#1E293B] hover:border-[#1E293B] transition-all duration-200 shadow-sm"
            >
                <FontAwesomeIcon icon={faDownload} className=" text-sm" />
                <span>Export</span>
            </button>
        </div>
    );

    // Filter by search query and status tab
    const filteredData = parkingData.filter(item => {
        let matchesSearch = true;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            matchesSearch =
                (item.OwnerName || "").toLowerCase().includes(query) ||
                (item.VehicleNumber || "").toLowerCase().includes(query) ||
                (item.SiteName || "").toLowerCase().includes(query);
        }

        let matchesStatus = true;
        if (statusFilter) {
            matchesStatus = item.IsActive === statusFilter;
        }

        return matchesSearch && matchesStatus;
    });

    const paginatedData = filteredData.slice((currentPage - 1) * pageInfo.pageSize, currentPage * pageInfo.pageSize).map((item, idx) => ({
        ...item,
        id: item.ParkingBookingId || `parking-${idx}`,
        no: (currentPage - 1) * pageInfo.pageSize + idx + 1
    }));

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6 min-h-screen bg-[#FFFFFF]">
            <div className='flex flex-col gap-1'>
                <Breadcrumbs />
                <SectionHeaderBar
                    heading="Parking Report"
                    subHeading="View all parking bookings."
                />
            </div>



            <CommonTable
                tableHeading="Parking Bookings"
                data={paginatedData}
                headers={tableHeaders}
                renderCell={renderCell}
                headerAction={headerAction}
                pagination={{
                    ...pageInfo,
                    pageNumber: currentPage,
                    totalRecords: filteredData.length,
                    totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                }}
                onPageChange={onPageChange}
                onLimitChange={handleLimitChange}
                loading={loading}
            />
        </div>
    );
};

export default ParkingReport;
